import { memo, forwardRef, useCallback, useMemo, useState } from 'react';
import { InputAdornment, Box } from '@mui/material';
import { CheckCircle, Error, Warning } from '@mui/icons-material';
import { InputComponentProps } from './Input.types';
import { StyledInput } from './Input.styles';

/**
 * Input Component
 *
 * Professional input/text field component following the green/teal design system.
 * Fully memoized for optimal performance in large ERP applications.
 *
 * @example
 * ```tsx
 * // Basic input
 * <Input
 *   label="Employee Name"
 *   placeholder="Enter employee name"
 *   value={name}
 *   onChange={(e) => setName(e.target.value)}
 * />
 *
 * // With validation
 * <Input
 *   label="Email Address"
 *   type="email"
 *   error={!!errors.email}
 *   errorMessage={errors.email}
 *   required
 * />
 *
 * // With character count
 * <Input
 *   label="Description"
 *   multiline
 *   rows={4}
 *   maxLength={500}
 *   showCharacterCount
 * />
 * ```
 */
export const Input = memo(
  forwardRef<HTMLDivElement, InputComponentProps>(function Input(
    {
      variant = 'outlined',
      size = 'md',
      type = 'text',
      label,
      placeholder,
      helperText,
      validationState = 'default',
      error = false,
      errorMessage,
      successMessage,
      startAdornment,
      endAdornment,
      disabled = false,
      readOnly = false,
      required = false,
      fullWidth = true,
      multiline = false,
      rows,
      maxRows,
      minRows,
      value,
      defaultValue,
      onChange,
      onFocus,
      onBlur,
      // onKeyPress,
      maxLength,
      showCharacterCount = false,
      // autoFocus = false,
      autoComplete,
      className,
      'data-testid': dataTestId,
      name,
      id,
      ...props
    },
    ref
  ) {
    // Track character count for controlled/uncontrolled inputs
    const [internalValue, setInternalValue] = useState(
      defaultValue?.toString() || ''
    );

    // Determine the current value (controlled or uncontrolled)
    const currentValue = value !== undefined ? value.toString() : internalValue;
    const characterCount = currentValue.length;

    // Determine validation state
    const finalValidationState = useMemo(() => {
      if (error || errorMessage) return 'error';
      if (successMessage) return 'success';
      return validationState;
    }, [error, errorMessage, successMessage, validationState]);

    // Determine helper text
    const finalHelperText = useMemo(() => {
      if (errorMessage) return errorMessage;
      if (successMessage) return successMessage;
      return helperText;
    }, [errorMessage, successMessage, helperText]);

    // Get validation icon
    const validationIcon = useMemo(() => {
      switch (finalValidationState) {
        case 'error':
          return <Error fontSize="small" />;
        case 'success':
          return <CheckCircle fontSize="small" />;
        case 'warning':
          return <Warning fontSize="small" />;
        default:
          return null;
      }
    }, [finalValidationState]);

    // Memoize change handler
    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const newValue = event.target.value;

        // Enforce max length if specified
        if (maxLength && newValue.length > maxLength) {
          return;
        }

        // Update internal value for uncontrolled input
        if (value === undefined) {
          setInternalValue(newValue);
        }

        // Call parent onChange
        onChange?.(event);
      },
      [maxLength, value, onChange]
    );

    // Memoize adornments
    const startAdornmentElement = useMemo(() => {
      if (!startAdornment) return undefined;
      return <InputAdornment position="start">{startAdornment}</InputAdornment>;
    }, [startAdornment]);

    const endAdornmentElement = useMemo(() => {
      // Show validation icon if no end adornment and validation state is not default
      if (
        !endAdornment &&
        validationIcon &&
        finalValidationState !== 'default'
      ) {
        return <InputAdornment position="end">{validationIcon}</InputAdornment>;
      }
      if (!endAdornment) return undefined;
      return <InputAdornment position="end">{endAdornment}</InputAdornment>;
    }, [endAdornment, validationIcon, finalValidationState]);

    // Character count helper text
    const helperTextWithCount = useMemo(() => {
      if (!showCharacterCount || !maxLength) {
        return finalHelperText;
      }

      const countText = (
        <Box
          component="span"
          sx={{
            float: 'right',
            fontSize: 'inherit',
            color:
              characterCount > maxLength * 0.9
                ? 'warning.main'
                : 'text.secondary',
          }}
        >
          {characterCount}/{maxLength}
        </Box>
      );

      if (!finalHelperText) {
        return countText;
      }

      return (
        <Box
          component="span"
          sx={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <span>{finalHelperText}</span>
          {countText}
        </Box>
      );
    }, [showCharacterCount, maxLength, finalHelperText, characterCount]);

    return (
      <StyledInput
        ref={ref}
        inputSize={size}
        validationState={finalValidationState}
        variant={variant}
        type={type}
        label={label}
        placeholder={placeholder}
        helperText={helperTextWithCount}
        error={finalValidationState === 'error'}
        disabled={disabled}
        required={required}
        fullWidth={fullWidth}
        multiline={multiline}
        rows={rows}
        maxRows={maxRows}
        minRows={minRows}
        value={value}
        defaultValue={defaultValue}
        onChange={handleChange}
        onFocus={onFocus}
        onBlur={onBlur}
        // onKeyPress={onKeyPress}
        // autoFocus={autoFocus}
        autoComplete={autoComplete}
        className={className}
        data-testid={dataTestId}
        name={name}
        id={id}
        InputProps={{
          readOnly,
          startAdornment: startAdornmentElement,
          endAdornment: endAdornmentElement,
          ...props.InputProps,
        }}
        {...(() => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const {
            onKeyPress: _kp,
            autoFocus: _af,
            inputProps: _ip,
            ...rest
          } = props;
          return rest;
        })()}
      />
    );
  })
);

Input.displayName = 'Input';

export default Input;
